import {
  ErrorMessage,
  Flex,
  Grid,
  Input,
  RadioBox,
  Select,
  Stack,
  Text,
  Toggle,
} from 'components/ui';
import VerifyPIC from 'components/verify-pic/verify-pic';
import { useUser } from 'hooks/use-user';
import { AddressNamed, States, TypesEnum } from 'lib/api/generated';
import { states } from 'lib/marketplace-utils';
import { isEqual } from 'lodash';
import { FC, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useMedia } from 'react-use';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import { useConfig } from '../../../hooks/use-config';
import { globalTheme } from '../../../styles/global-theme';
const AddressAutoComplete = dynamic(
  () => import('../../address-autocomplete'),
  {
    ssr: false,
  }
);

import dynamic from 'next/dynamic';
import { CreateOrgFormData } from '../common';
interface OrgDetailsProp {
  selectedType: Array<TypesEnum>;
}

type FormData = AddressNamed;

const ButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${globalTheme.space[1]};

  @media screen and (max-width: ${breakpoints.medium}) {
    justify-content: center;
  }
`;

const AutoAddressWrapper = styled.div<{ blur: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${globalTheme.space[1]};
  display: ${(props) => (props.blur ? 'none' : 'unset')};
  pointer-events: ${(props) => (props.blur ? 'none' : 'unset')};
  user-select: ${(props) => (props.blur ? 'none' : 'unset')};
`;

const ManualModeText = styled(Text)`
  cursor: pointer;
  @media screen and (max-width: ${breakpoints.medium}) {
    font-size: 1rem;
  }
`;
const ManualText = styled(Text)`
  @media screen and (max-width: ${breakpoints.medium}) {
    font-size: 1rem;
  }
`;

const StateInput = styled(Input)`
  display: none !important;
`;

enum AgentEnum {
  NEVER = 'Never',
  SOMETIMES = 'Sometimes',
  ALWAYS = 'Always',
}

export const OrgText = styled(Text)<{ justifyStart?: boolean }>`
  @media screen and (max-width: ${breakpoints.medium}) {
    font-size: 1.25rem;
  }
`;

export const OrgDetails: FC<OrgDetailsProp> = ({ selectedType }) => {
  const {
    register,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<CreateOrgFormData>();

  const use_agent = useWatch({ control, name: 'use_agent' });
  const pic = useWatch({ control, name: 'pic' });
  const { data: user } = useUser();
  const [manualAddressMode, setManualAddressMode] = useState<boolean>(false);
  const [resetState, setResetState] = useState<boolean>(false);
  const { data: config } = useConfig();
  const handleAddressFillType = () => {
    setManualAddressMode(!manualAddressMode);
  };

  const stateValues: { id: string; value: string }[] = Object.values(
    States
  ).map((key) => ({
    id: key,
    value: key,
  }));

  stateValues.push({ id: 'default state', value: '' });

  const getStateValueFromFullName = (fullName: string): States | undefined => {
    if (!config?.states) {
      return undefined;
    }

    const stateEntries = Object.entries(config.states);
    const matchingEntry = stateEntries.find(
      ([key, value]) => value === fullName
    );

    if (matchingEntry) {
      const [stateCode] = matchingEntry;
      return stateCode as States;
    } else {
      return undefined;
    }
  };

  const handleSBoxRespRetrieve = (resp: any) => {
    const response = resp?.features[0]?.properties;
    const geoResponse = resp?.features[0]?.geometry;
    setValue('address.address', response?.address, { shouldValidate: true });
    setValue('address.postcode', response?.context?.postcode?.name, {
      shouldValidate: true,
    });
    setValue(
      'address.suburb',
      response?.context?.locality
        ? response?.context?.locality?.name
        : response?.context?.place?.name,
      { shouldValidate: true }
    );

    {
      if (response?.context?.region?.region_code) {
        setResetState(false);
        setValue('address.state', response?.context?.region?.region_code, {
          shouldValidate: true,
        });
      } else {
        setValue(
          'address.state',
          stateValues.find((state) => state.id === 'default state')
            ?.value as States,
          { shouldValidate: true }
        );
        setResetState(true);
      }
    }
    setValue(
      'address.gps',
      {
        longitude: geoResponse?.coordinates[0],
        latitude: geoResponse?.coordinates[1],
      },
      { shouldValidate: true }
    );
  };

  useEffect(() => {
    if (user && errors) {
      if (errors.email === undefined && !getValues('email')) {
        setValue('email', user?.email ? user?.email : '', {
          shouldValidate: false,
        });
      }
      if (errors.phone === undefined && !getValues('phone')) {
        setValue(
          'phone',
          user?.phone ? user?.phone : user?.mobile ? user?.mobile : '',
          {
            shouldValidate: false,
          }
        );
      }
      if (errors.pic === undefined && !getValues('pic')) {
        setValue(
          'pic',
          user?.property_identification_code
            ? user?.property_identification_code
            : '',
          {
            shouldValidate: false,
          }
        );
      }
      if (errors.name === undefined && !getValues('name')) {
        setValue('name', user?.company_name ? user?.company_name : '', {
          shouldValidate: false,
        });
      }

      if (errors.states === undefined && getValues('states').length === 0) {
        setValue('states', user?.state ? [user?.state] : [], {
          shouldValidate: false,
        });
      }
      if (errors.agent_branch === undefined && !getValues('agent_branch')) {
        setValue('agent_branch', user?.town ? user?.town : '', {
          shouldValidate: false,
        });
      }
    }
  }, [user, errors]);

  function preprocessData(data: FormData) {
    if (manualAddressMode) {
      data.gps = null;
    }
    if (!data.gps) {
      data.gps = null;
    }
    return data;
  }

  const picTypes = [
    TypesEnum.Backgrounder,
    TypesEnum.Breeder,
    TypesEnum.HobbyFarmer,
    TypesEnum.Trader,
    TypesEnum.Feedlot,
  ];

  const stateTypes = [
    TypesEnum.Butcher,
    TypesEnum.CommissionBuyer,
    TypesEnum.Feedlot,
    TypesEnum.LiveExporter,
    TypesEnum.Processor,
    TypesEnum.Trader,
  ];
  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`, false);

  return (
    <Stack space={2}>
      {/* PIC code */}
      {selectedType.some((type) => picTypes.includes(type)) && (
        <VerifyPIC
          setValue={setValue}
          inputProps={{ ...register('pic') }}
          errors={errors}
          pic={pic}
          isRequired
        />
      )}
      {/* Trading name */}
      <Input
        type='text'
        label='Trading name'
        isRequired
        placeholder='Your Business/Trading Name'
        {...register('name')}
        state={errors.name && 'error'}
        message={errors.name?.message}
        data-cy='tradingName'
        orgFontSize={3}
        isMobile={isMobile}
      />
      <Stack space={0}>
        <OrgText justifyStart required fontWeight={'bold'}>
          Enter your address
        </OrgText>
        <AutoAddressWrapper blur={manualAddressMode}>
          <AddressAutoComplete
            handleRetrieve={handleSBoxRespRetrieve}
            disabled={manualAddressMode}
            placeholder='Street address'
            {...register('address.address')}
            value={getValues('address.address')}
            onChange={(value: string) => {
              setValue('address.address', value, { shouldValidate: true });
            }}
          />
          {errors?.address?.address && (
            <ErrorMessage variant='compact'>
              {errors?.address?.address?.message}
            </ErrorMessage>
          )}
          <Grid columns='2fr repeat(2, 1fr)' gap={2} stackOnSmall>
            <Input
              type='text'
              label='Suburb'
              isRequired
              disabled={manualAddressMode}
              autoComplete='address-level2'
              {...register('address.suburb')}
              state={errors?.address?.suburb && 'error'}
              message={errors?.address?.suburb?.message}
              orgFontSize={3}
              isMobile={isMobile}
            />
            <Controller
              control={control}
              name='address.state'
              render={({ field: { onChange, value } }) => {
                return (
                  <Select
                    label='State'
                    isRequired
                    items={stateValues.filter((state) => {
                      return state.id !== 'default state';
                    })}
                    placeholder='&nbsp;'
                    triggerReset={resetState}
                    disabled={manualAddressMode}
                    onChange={(val) => {
                      onChange(val.id);
                    }}
                    value={value}
                    state={errors?.address?.state && 'error'}
                    message={errors?.address?.state?.message}
                    orgFontSize={3}
                    isMobile={isMobile}
                  />
                );
              }}
            />
            <StateInput
              type='text'
              label='State'
              disabled={manualAddressMode}
              autoComplete='address-level1'
              {...register('address.state')}
              state={errors?.address?.suburb && 'error'}
              message={errors?.address?.suburb?.message}
            />
            <Input
              type='text'
              label='Postcode'
              isRequired
              disabled={manualAddressMode}
              autoComplete='postal-code'
              {...register('address.postcode')}
              state={errors?.address?.postcode && 'error'}
              message={errors?.address?.postcode?.message}
              orgFontSize={3}
              isMobile={isMobile}
            />
          </Grid>
        </AutoAddressWrapper>

        {manualAddressMode && (
          <Stack space={0}>
            <Input
              placeholder='Street address'
              autoComplete='street-address'
              {...register('address.address')}
              state={errors?.address?.address && 'error'}
              message={errors?.address?.address?.message}
              orgFontSize={3}
              isMobile={isMobile}
            />
            <Grid columns='2fr repeat(2, 1fr)' gap={2} stackOnSmall>
              <Input
                type='text'
                label='Suburb'
                isRequired
                autoComplete='address-level2'
                {...register('address.suburb')}
                state={errors?.address?.suburb && 'error'}
                message={errors?.address?.suburb?.message}
                orgFontSize={3}
                isMobile={isMobile}
              />
              <Controller
                control={control}
                name='address.state'
                render={({ field: { onChange, value } }) => {
                  return (
                    <Select
                      label='State'
                      isRequired
                      items={stateValues}
                      onChange={(val) => {
                        onChange(val.id);
                      }}
                      value={
                        getStateValueFromFullName(getValues('address.state')) ||
                        value
                      }
                      state={errors?.address?.state && 'error'}
                      message={errors?.address?.state?.message}
                      orgFontSize={3}
                      isMobile={isMobile}
                    />
                  );
                }}
              />
              <Input
                type='text'
                label='Postcode'
                isRequired
                autoComplete='postal-code'
                {...register('address.postcode')}
                state={errors?.address?.postcode && 'error'}
                message={errors?.address?.postcode?.message}
                orgFontSize={3}
                isMobile={isMobile}
              />
            </Grid>
          </Stack>
        )}
      </Stack>

      {selectedType.includes(TypesEnum.Agent) && (
        <Text fontSize={4} fontWeight='bold'>
          About your livestock agency
        </Text>
      )}

      <Flex alignItems='center' wrap='wrap'>
        <ManualText maxWidth={'max-content'}>
          Can’t find your address? &nbsp;{' '}
        </ManualText>
        <ManualModeText
          maxWidth={'max-content'}
          color='blue'
          onClick={() => handleAddressFillType()}
        >
          {manualAddressMode
            ? 'Try address auto-fill mode.'
            : 'Enter it manually.'}
        </ManualModeText>
      </Flex>

      {/* Agent section */}
      {selectedType.includes(TypesEnum.Agent) && (
        <>
          <Input
            type='text'
            label='Agency name'
            isRequired
            placeholder=''
            {...register('agent_name')}
            state={errors.agent_name && 'error'}
            message={errors.agent_name?.message}
            data-cy='agent-name'
            orgFontSize={3}
            isMobile={isMobile}
          />
          <Input
            type='text'
            label='Branch/Location'
            isRequired
            {...register('agent_branch')}
            state={errors.agent_branch && 'error'}
            message={errors.agent_branch?.message}
            data-cy='agent_branch'
            orgFontSize={3}
            isMobile={isMobile}
          />

          {/* Agent referral */}
          <OrgText justifyStart fontWeight='bold' aria-label={'agent'}>
            Can we refer producers to you if they need an agent?{' '}
          </OrgText>
          <Toggle
            toggleOnContent='Yes'
            toggleOffContent='No'
            {...register('agent_allow_referral')}
          />
        </>
      )}

      {/* Use Agent */}
      {!isEqual(selectedType, [TypesEnum.Agent]) && (
        <>
          <OrgText justifyStart fontWeight='bold' aria-label={'agent'}>
            Do you use an agent?
          </OrgText>
          <Grid columns={'repeat(3, max-content)'} gap={0}>
            {Object.entries(AgentEnum).map(([key, value]) => (
              <RadioBox
                key={key}
                data-cy={`${value}Box`}
                label={value}
                value={key}
                {...register('use_agent')}
                orgFontSize={3}
                isMobile={isMobile}
              />
            ))}
          </Grid>
        </>
      )}

      {/* Agency Section */}
      {!isEqual(selectedType, [TypesEnum.Agent]) &&
        (use_agent === 'SOMETIMES' || use_agent === 'ALWAYS') && (
          <Grid columns={3} gap={2} stackOnSmall>
            <Input
              type='text'
              label="My Agent's name"
              placeholder=''
              {...register('agency_name')}
              state={errors.agency_name && 'error'}
              message={errors.agency_name?.message}
              data-cy='agent-name'
              orgFontSize={3}
              isMobile={isMobile}
            />
            <Input
              type='text'
              label='Their Agency'
              {...register('agency_agency')}
              state={errors.agency_agency && 'error'}
              message={errors.agency_agency?.message}
              data-cy='agent_agency'
              orgFontSize={3}
              isMobile={isMobile}
            />
            <Input
              type='text'
              label='Their Branch/Location'
              {...register('agency_branch')}
              state={errors.agency_branch && 'error'}
              message={errors.agency_branch?.message}
              data-cy='agent_branch'
              orgFontSize={3}
              isMobile={isMobile}
            />
          </Grid>
        )}

      {/* States */}
      {selectedType.some((type) => stateTypes.includes(type)) && (
        <>
          <OrgText
            required
            justifyStart
            fontWeight='bold'
            aria-label={'states'}
          >
            What states do you purchase livestock from?
          </OrgText>
          <ButtonContainer>
            {states.map((state, idx) => (
              <RadioBox
                key={idx}
                data-cy='state'
                type='checkbox'
                label={state}
                value={state}
                {...register('states')}
                orgFontSize={3}
                isMobile={isMobile}
              />
            ))}
          </ButtonContainer>
          {errors.states && (
            <ErrorMessage>{errors?.states?.message}</ErrorMessage>
          )}
        </>
      )}

      {/* Email */}
      <Grid columns={2} gap={2} stackOnSmall>
        <Input
          type='email'
          label='Company email address'
          isRequired
          placeholder='calvin@email.com'
          {...register('email')}
          state={errors.email && 'error'}
          message={errors.email?.message}
          autoComplete='email'
          data-cy='emailField'
          orgFontSize={3}
          isMobile={isMobile}
        />

        {/* Phone */}
        <Input
          type='tel'
          label='Company phone number'
          isRequired
          placeholder='0451 553 453'
          {...register('phone')}
          state={errors.phone && 'error'}
          message={errors.phone?.message}
          autoComplete='tel'
          data-cy='phoneNumberField'
          orgFontSize={3}
          isMobile={isMobile}
        />
      </Grid>
    </Stack>
  );
};
